import React, { useCallback } from 'react'

import Image from 'next/image'
import { useRouter } from 'next/navigation'

import { CalendarDot, CaretLeft, MapPin } from '@phosphor-icons/react'
import { Column, IconButton, Text } from 'Components/UI'
import { DateTime } from 'luxon'
import { EventItem } from 'Types/emajinAPI'

import { GOOGLE_MAP_URL } from 'Constants/Ids'
import { EVENT_DETAILS } from 'Constants/Paths'

import {
  CompoundRow,
  Container,
  ContainerProps,
  ImageContainer,
} from './styles'

type Props = ContainerProps & {
  event?: EventItem
}

function WebFormEventPresenter({ event, ...rest }: Props) {
  const router = useRouter()

  const handleLocationClick = useCallback(() => {
    if (!event || !event.coordinate) return

    window.open(
      `${GOOGLE_MAP_URL}${event.coordinate.lat},${event.coordinate.lng}`,
      '_blank',
    )
  }, [event])

  const handleReturnClick = useCallback(() => {
    if (!event) return

    router.replace(EVENT_DETAILS(event.id))
  }, [event, router])

  if (!event) return null

  return (
    <Container {...rest}>
      <Column fullWidth>
        <Text disabled mb={7} overline uppercase>
          Attending RSVP
        </Text>

        <ImageContainer mb={7}>
          {event.attachments?.[0]?.url && (
            <Image
              alt={event.name}
              height={300}
              src={event.attachments[0].url}
              width={500}
            />
          )}
        </ImageContainer>

        <Text header5 mb={4} primary>
          {event.name}
        </Text>

        <Text body2 mb={7} secondary>
          {event.address}
        </Text>

        <CompoundRow>
          <CalendarDot />

          <Text mb={5} overline primary uppercase>
            {DateTime.fromISO(event.date).toFormat('LLLL dd, yyyy')}
          </Text>
        </CompoundRow>

        <CompoundRow>
          <MapPin />

          <Column alignStart gap={2} mb={7}>
            <IconButton
              disabled={!event.coordinate}
              onClick={handleLocationClick}
            >
              <Text overline uppercase>
                {event.location}
              </Text>
            </IconButton>

            <Text caption secondary>
              {event.venueName}
            </Text>
          </Column>
        </CompoundRow>
      </Column>

      <IconButton onClick={handleReturnClick}>
        <CaretLeft weight="bold" />
        Go to Event details
      </IconButton>
    </Container>
  )
}

export default WebFormEventPresenter
